<script setup>
import { ref, computed } from 'vue';
import { Head, Link, usePage } from '@inertiajs/vue3';
import FrontLayout from '@/Layouts/FrontLayout.vue';
import PageHero from '@/Components/Front/PageHero.vue';
import SocialShare from '@/Components/Front/SocialShare.vue';

const props = defineProps({
    openTrip: Object,
    relatedTrips: Array,
    seoData: Object,
    setting: Object, // passed from controller or layout
});

const page = usePage();
const appSetting = computed(() => props.setting || page.props.setting || {});

const activeTab = ref('overview'); // overview | requirements

const waLink = computed(() => {
    const phone = appSetting.value.setting_no_wa || '';
    const text = encodeURIComponent(`Halo, saya tertarik dengan trip ${props.openTrip.title}. Mohon informasi lebih lanjut.`);
    return `https://wa.me/${phone}?text=${text}`;
});
</script>

<template>
    <FrontLayout :setting="appSetting" :title="seoData?.title || openTrip.title">
        <Head>

            <meta name="description" :content="seoData?.meta_description" />
            <meta name="keywords" :content="seoData?.meta_keywords" />
            <link v-if="seoData?.canonical_url" rel="canonical" :href="seoData?.canonical_url" />
            
            <meta property="og:title" :content="seoData?.og_title" />
            <meta property="og:description" :content="seoData?.og_description" />
            <meta property="og:image" :content="seoData?.og_image" />
            <meta property="og:url" :content="seoData?.og_url" />
            <meta property="og:type" :content="seoData?.og_type" />
            <meta property="og:site_name" content="Gotransport Banyuwangi" />
            
            <meta name="twitter:title" :content="seoData?.twitter_title" />
            <meta name="twitter:description" :content="seoData?.twitter_description" />
            <meta name="twitter:image" :content="seoData?.twitter_image" />
        </Head>

        <!-- Hero Section -->
        <PageHero 
            :title="openTrip.title" 
            :subtitle="`Paket Wisata ${openTrip.destination}`"
            :breadcrumbs="[
                { label: 'Beranda', url: '/' },
                { label: 'Open Trip', url: '/trip-wisata-banyuwangi' },
                { label: openTrip.title }
            ]"
        >
            <div class="flex flex-wrap justify-center gap-2 mt-4">
                <span class="inline-block bg-white/20 backdrop-blur-sm text-white px-3 py-1.5 text-sm font-medium rounded-full border border-white/20">
                    <i class="fa fa-map-marker mr-1"></i>{{ openTrip.destination }}
                </span>
                <span v-if="openTrip.featured" class="inline-block bg-yellow-400 text-gray-900 px-3 py-1.5 text-sm font-bold rounded-full shadow-lg">
                    <i class="fa fa-star mr-1"></i>Unggulan
                </span>
            </div>
        </PageHero>

        <!-- Detail Content -->
        <section class="py-8 md:py-10 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800">
            <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl">
                <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div class="lg:col-span-2">
                        <article class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm">
                            <!-- Image -->
                            <div v-if="openTrip.image" class="w-full bg-gray-100">
                                <img :src="`/storage/open-trips/${openTrip.image}`" 
                                     class="w-full h-auto object-cover" 
                                     :alt="openTrip.title" 
                                     loading="lazy">
                            </div>
                            
                            <div class="p-4">
                                <!-- Title -->
                                <h1 class="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-start gap-3">
                                    <i class="fa fa-map text-blue-600 dark:text-blue-400 mt-1"></i>
                                    {{ openTrip.title }}
                                </h1>
                                
                                <!-- Tabs Navigation -->
                                <div class="flex border-b border-gray-200 dark:border-gray-700 mb-6">
                                    <button 
                                        @click="activeTab = 'overview'"
                                        :class="[
                                            'px-6 py-3 font-medium text-sm md:text-base border-b-2 transition-colors flex-1 md:flex-none text-center',
                                            activeTab === 'overview' 
                                                ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400' 
                                                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                                        ]"
                                    >
                                        <i class="fa fa-info-circle mr-2"></i>Deskripsi
                                    </button>
                                    <button 
                                        v-if="openTrip.requirements"
                                        @click="activeTab = 'requirements'"
                                        :class="[
                                            'px-6 py-3 font-medium text-sm md:text-base border-b-2 transition-colors flex-1 md:flex-none text-center',
                                            activeTab === 'requirements' 
                                                ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400' 
                                                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                                        ]"
                                    >
                                        <i class="fa fa-list-ul mr-2"></i>Persyaratan
                                    </button>
                                </div>
                                
                                <!-- Tab Content: Overview -->
                                <div v-if="activeTab === 'overview'" class="animate-fadeIn">
                                    <div class="prose dark:prose-invert prose-sm md:prose-base max-w-none text-gray-700 dark:text-gray-300">
                                        <div v-html="openTrip.description"></div>
                                    </div>
                                    
                                    <div class="mt-8 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded-r-lg">
                                        <div class="flex items-start gap-3">
                                            <div class="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center text-blue-600 dark:text-blue-300 flex-shrink-0">
                                                <i class="fa fa-map-marker"></i>
                                            </div>
                                            <div>
                                                <h4 class="font-bold text-gray-900 dark:text-white mb-1">Lokasi Destinasi</h4>
                                                <p class="text-gray-700 dark:text-gray-300">{{ openTrip.destination }}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Tab Content: Requirements -->
                                <div v-if="activeTab === 'requirements' && openTrip.requirements" class="animate-fadeIn">
                                    <div class="prose dark:prose-invert prose-sm md:prose-base max-w-none text-gray-700 dark:text-gray-300">
                                        <div class="whitespace-pre-line">{{ openTrip.requirements }}</div>
                                    </div>
                                </div>
                                
                                <!-- Social Share -->
                                <div class="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
                                    <SocialShare
                                        :url="seoData?.canonical_url || page.props.ziggy.location"
                                        :title="openTrip.title"
                                        layout="horizontal"
                                    />
                                </div>
                            </div>
                        </article>
                    </div>
                    
                    <!-- Sidebar -->
                    <aside class="lg:col-span-1 space-y-6">
                        <!-- Info Card -->
                        <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 shadow-sm">
                            <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
                                <i class="fa fa-info-circle text-blue-600 dark:text-blue-400"></i>
                                Info Trip
                            </h3>
                            
                            <div class="space-y-4">
                                <div class="flex justify-between items-center">
                                    <span class="text-gray-600 dark:text-gray-400 text-sm">Destinasi</span>
                                    <span class="font-bold text-gray-900 dark:text-white text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{{ openTrip.destination }}</span>
                                </div>
                                
                                <div v-if="openTrip.featured" class="flex justify-between items-center">
                                    <span class="text-gray-600 dark:text-gray-400 text-sm">Status</span>
                                    <span class="inline-flex items-center gap-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 px-2 py-1 text-xs font-bold rounded">
                                        <i class="fa fa-star text-[10px]"></i> Unggulan
                                    </span>
                                </div>
                                
                                <div class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-3 rounded-lg flex items-center gap-2">
                                    <i class="fa fa-check-circle text-green-600 dark:text-green-400 text-lg"></i>
                                    <span class="text-green-800 dark:text-green-400 font-bold text-sm">Trip Tersedia</span>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Booking Box -->
                        <div class="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-4 text-white text-center shadow-lg shadow-blue-200">
                            <div class="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
                                <i class="fa fa-calendar-check-o text-3xl"></i>
                            </div>
                            <h3 class="text-xl font-bold mb-2">Booking Sekarang</h3>
                            <p class="text-blue-100 text-sm mb-6">Amankan slot Anda untuk pengalaman wisata tak terlupakan!</p>
                            
                            <div class="space-y-3">
                                <a :href="waLink" 
                                   target="_blank" 
                                   class="flex items-center justify-center w-full px-4 py-3 bg-green-500 hover:bg-green-400 text-white font-bold rounded-lg transition-colors shadow-lg">
                                    <i class="fa fa-whatsapp text-xl mr-2"></i> Chat WhatsApp
                                </a>
                                <Link href="/contact" 
                                   class="flex items-center justify-center w-full px-4 py-3 bg-white/10 hover:bg-white/20 text-white font-bold rounded-lg transition-colors border border-white/30">
                                    <i class="fa fa-envelope mr-2"></i> Hubungi Kami
                                </Link>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </section>

        <!-- Related Trips -->
        <section v-if="relatedTrips?.length > 0" class="relative py-12 md:py-16 bg-gradient-to-br from-slate-900 to-slate-800 overflow-hidden">
             
             <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl relative z-10">
                <div class="text-center mb-10">
                    <span class="inline-block bg-yellow-400 text-slate-900 px-3 py-1 font-bold text-xs tracking-wider mb-3 transform -rotate-1 rounded shadow-lg">
                        ✨ JELAJAHI LAINNYA
                    </span>
                    <h2 class="text-3xl md:text-4xl font-bold text-white mb-3">Petualangan Menanti Anda!</h2>
                    <p class="text-slate-300 max-w-2xl mx-auto">Temukan pengalaman tak terlupakan di destinasi menarik lainnya.</p>
                </div>
                
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <Link v-for="trip in relatedTrips" :key="trip.id" :href="`/trip-wisata-banyuwangi/${trip.slug}`"
                          class="group bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-xl transform transition-all hover:-translate-y-2 hover:shadow-2xl">
                        <div class="relative h-56 overflow-hidden">
                            <img v-if="trip.image" :src="`/storage/open-trips/${trip.image}`" 
                                 class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
                                 :alt="trip.title">
                            <div v-else class="w-full h-full bg-gray-200 flex items-center justify-center">
                                <i class="fa fa-image text-gray-400 text-4xl"></i>
                            </div>
                            <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-80"></div>
                            <div class="absolute bottom-4 left-4">
                                <span class="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded mb-2 inline-block">
                                    {{ trip.destination }}
                                </span>
                                <h3 class="text-white font-bold text-lg leading-tight group-hover:text-blue-300 transition-colors">
                                    {{ trip.title }}
                                </h3>
                            </div>
                        </div>
                        <div class="p-4">
                            <p class="text-gray-600 dark:text-gray-400 text-sm line-clamp-2 mb-4">
                                {{ trip.description ? trip.description.replace(/<[^>]*>?/gm, '') : 'Lihat detail paket wisata ini.' }}
                            </p>
                            <div class="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700">
                                <span class="text-blue-600 dark:text-blue-400 font-bold text-sm group-hover:underline">Lihat Detail</span>
                                <i class="fa fa-arrow-right text-blue-600 dark:text-blue-400 transform group-hover:translate-x-1 transition-transform"></i>
                            </div>
                        </div>
                    </Link>
                </div>
             </div>
        </section>
    </FrontLayout>
</template>
